/**
 * Render-time placement of moving things (docs §3):
 * aircraft / ships follow motion segments, rendered at estimated tick − 1, with a short
 * error-blend so course changes never teleport. (Missiles need no interpolation: the scene
 * places them with the shared `missilePos()` at the estimated tick.)
 *
 * A segment is one straight leg and keeps its start tick for the whole leg (a ship's open-sea
 * leg runs a minute or more); the server's 1 s heartbeat re-sends it unchanged. So the cap on
 * extrapolation counts from the last time we *heard* of the entity, not from the segment start.
 */
import { TICK_HZ, type EntityId, type EntityKind, type EntityView, type Vec2 } from '@sa/sim';

/** Never extrapolate further than this past the last update we heard (server heartbeat is 1 s). */
export const MAX_EXTRAP_TICKS = 25;
/** error blend half-life, ms */
const BLEND_HALF_LIFE_MS = 90;

const MOBILE: ReadonlySet<EntityKind> = new Set<EntityKind>(['fighter', 'bomber', 'sub', 'carrier']);

export function isMobileKind(kind: EntityKind): boolean {
  return MOBILE.has(kind);
}

/**
 * Position on an entity's motion segment at (fractional) tick, extrapolated at most
 * `MAX_EXTRAP_TICKS` past `heardTick` (the last tick an update for it arrived; default: the
 * segment start). Structures return x, y.
 */
export function segmentPos(
  e: Pick<EntityView, 'x' | 'y' | 'vx' | 'vy' | 'segTick'>,
  tick: number,
  out: Vec2 = { x: 0, y: 0 },
  heardTick: number = e.segTick,
): Vec2 {
  const dt = (Math.min(Math.max(heardTick, e.segTick) + MAX_EXTRAP_TICKS, Math.max(e.segTick, tick)) - e.segTick) / TICK_HZ;
  out.x = e.x + e.vx * dt;
  out.y = e.y + e.vy * dt;
  return out;
}

/** Where a unit is at `tick` going by its latest segment (for rules: range checks, not drawing). */
export function posNow(e: Pick<EntityView, 'x' | 'y' | 'vx' | 'vy' | 'segTick'>, tick: number): Vec2 {
  return segmentPos(e, tick, { x: 0, y: 0 }, tick);
}

/** Heading in radians (atan2 in map space) or null if not moving. */
export function headingOf(e: Pick<EntityView, 'vx' | 'vy'>): number | null {
  return e.vx === 0 && e.vy === 0 ? null : Math.atan2(e.vy, e.vx);
}

interface BlendState {
  /** the view object last seen (every upsert, heartbeats included, is a new object) */
  ref: EntityView;
  /** display tick at which it arrived */
  heard: number;
  segTick: number;
  segX: number;
  segY: number;
  ox: number;
  oy: number;
  lastX: number;
  lastY: number;
  lastMs: number;
}

/**
 * Smooths segment switches: when a new segment arrives, the rendered position keeps the
 * old visual position and decays the difference over ~0.3 s.
 */
export class MotionSmoother {
  private s = new Map<EntityId, BlendState>();

  pos(e: EntityView, tick: number, nowMs: number, out: Vec2 = { x: 0, y: 0 }): Vec2 {
    let st = this.s.get(e.id);
    if (st && st.ref !== e) {
      st.ref = e;
      st.heard = tick;
    }
    segmentPos(e, tick, out, st ? st.heard : tick);
    if (!st) {
      st = { ref: e, heard: tick, segTick: e.segTick, segX: e.x, segY: e.y, ox: 0, oy: 0, lastX: out.x, lastY: out.y, lastMs: nowMs };
      this.s.set(e.id, st);
    } else if (st.segTick !== e.segTick || st.segX !== e.x || st.segY !== e.y) {
      // new segment: carry the visual error, then decay it
      st.ox = st.lastX - out.x;
      st.oy = st.lastY - out.y;
      // a big jump (respawn / long gap) is not smoothed
      if (st.ox * st.ox + st.oy * st.oy > 9) st.ox = st.oy = 0;
      st.segTick = e.segTick;
      st.segX = e.x;
      st.segY = e.y;
    }
    const dt = Math.max(0, nowMs - st.lastMs);
    const k = Math.pow(0.5, dt / BLEND_HALF_LIFE_MS);
    st.ox *= k;
    st.oy *= k;
    out.x += st.ox;
    out.y += st.oy;
    st.lastX = out.x;
    st.lastY = out.y;
    st.lastMs = nowMs;
    return out;
  }

  /** Forget entities no longer present. */
  retain(alive: (id: EntityId) => boolean): void {
    for (const id of this.s.keys()) if (!alive(id)) this.s.delete(id);
  }
}
